/** @file Bone form: setup length and inherit, then the seven channels, setup or keyed at the playhead. */
import { useState } from 'react';
import { useStore } from 'zustand';
import type { CommandCall } from '@pose2d/format/types';
import { choiceOptions } from '../../forms/choiceOptions';
import { FormStatus } from '../../forms/FormStatus';
import { submitCalls, type SubmitResult } from '../../forms/submitCalls';
import { playheadStore } from '../../stores/playheadStore';
import { toolStore } from '../../stores/toolStore';
import { NumberField } from '../../ui-kit/NumberField';
import { Select } from '../../ui-kit/Select';
import { boneFormModel, type BoneEditContext } from './boneFormModel';
import { EntityHeader } from './EntityHeader';
import type { EntityViewProps, FormKind } from './formKindOf';
import { TRANSFORM_CHANNELS } from './transformChannels.data';
import { useStoppedFrame } from './useStoppedFrame';

const BONE: FormKind = 'bone';
const MARK = { here: '◆', other: '◇', none: '' } as const;
const DEGREE = '°';

/** The seven channel fields with their key markers. */
function ChannelFields({ doc, id, ctx, run, t }: Pick<EntityViewProps, 'doc' | 'id' | 't'> & { ctx: BoneEditContext; run: (c: CommandCall) => void }) {
  const rows = boneFormModel.rows(doc, id, ctx);
  return <>{rows.map((row, i) => {
    const spec = TRANSFORM_CHANNELS[i];
    const title = row.key === 'here' ? t('panels.inspector.keyHere') : row.key === 'other' ? t('panels.inspector.keyOther') : undefined;
    return (
      <span key={row.channel} className="p2-inspector-row">
        <NumberField label={t(`panels.inspector.channels.${row.channel}`)} value={row.value} step={spec?.step ?? 1} {...(spec?.degrees === true ? { unit: DEGREE } : {})}
          disabled={!row.editable} onCommit={(v) => { run(boneFormModel.editCall(id, row.channel, v, ctx)); }} />
        <span className="p2-key-marker" title={title}>{MARK[row.key]}</span>
      </span>
    );
  })}</>;
}

/** Length and inherit are setup properties (disabled in Animate mode and on generated socket bones). */
export function BoneForm({ doc, id, t }: Omit<EntityViewProps, 'kind'>) {
  const mode = useStore(toolStore, (s) => s.mode);
  const animation = useStore(playheadStore, (s) => s.animation);
  const frame = useStoppedFrame();
  const [status, setStatus] = useState<SubmitResult | null>(null);
  const bone = doc.bones[id];
  if (bone === undefined) return null;
  const run = (call: CommandCall): void => { void submitCalls([call]).then(setStatus); };
  const setupEdits = mode === 'setup' && bone.kind === undefined;
  const parent = bone.parent === null ? '—' : (doc.bones[bone.parent]?.name ?? bone.parent);
  return (
    <div className="p2-inspector-entity">
      <EntityHeader doc={doc} kind={BONE} id={id} t={t} />
      <p className="p2-inspector-note">{t('panels.inspector.parent')}: {parent}</p>
      {bone.kind !== undefined && <p className="p2-inspector-note">{t('panels.inspector.generated')}</p>}
      {mode === 'animate' && animation === null && <p className="p2-inspector-note">{t('panels.inspector.chooseAnimation')}</p>}
      <NumberField label={t('panels.inspector.length')} value={bone.length} min={0} disabled={!setupEdits} onCommit={(v) => { run({ name: 'bone.setLength', input: { target: id, value: v } }); }} />
      <Select label={t('panels.inspector.inherit')} value={bone.inherit} disabled={!setupEdits} options={choiceOptions('bone.setInherit', 'value').map((o) => ({ value: o, label: o }))}
        onChange={(v) => { run({ name: 'bone.setInherit', input: { target: id, value: v } }); }} />
      <ChannelFields doc={doc} id={id} ctx={{ mode, animation, frame }} run={run} t={t} />
      <FormStatus status={status} t={t} />
    </div>
  );
}
